import type { Admission } from '@/api/admissions';
import type { Lead, LeadAssignee, LeadSource, LeadStage, RefusalReason } from '@/api/leads';

export type RecruitmentView = 'board' | 'list' | 'analytics';
export const STAGES = ['new', 'contacted', 'accepted', 'enrolled', 'refused'] as const;
export const STAGE_KEYS: Record<LeadStage, string> = {
  new: 'admin.admissions.status.new',
  contacted: 'admin.admissions.status.contacted',
  accepted: 'admin.admissions.status.accepted',
  enrolled: 'admin.admissions.status.enrolled',
  refused: 'admin.admissions.status.rejected',
};
export type Candidate = {
  id: string;
  lead: Lead | null;
  admission: Admission | null;
  stage: LeadStage;
  fullName: string;
  contact: string;
  subjectId: string | null;
  source: LeadSource | null;
  assignee: LeadAssignee | null;
  createdAt: string;
  note: string | null;
  refusalReasonId: string | null;
  refusalReason: RefusalReason | null;
};
export type CandidateFilters = {
  query?: string;
  stage?: LeadStage | '';
  subject?: string;
  source?: string;
  assignee?: string;
  refusalReason?: string;
};

function candidate(lead: Lead | null, admission: Admission | null): Candidate {
  if (!lead && !admission) throw new Error('Candidate requires a lead or admission');
  const stage = admission
    ? admission.enrolled_student_id
      ? 'enrolled'
      : admission.status === 'rejected'
        ? 'refused'
        : admission.status
    : lead!.stage;
  return {
    id: lead?.id ?? admission!.lead_id ?? `admission:${admission!.id}`,
    lead,
    admission,
    stage,
    fullName: admission?.full_name ?? lead!.full_name,
    contact: admission?.contact ?? lead!.phone,
    subjectId: admission?.subject_id ?? null,
    source: lead?.source ?? admission?.source ?? null,
    assignee: lead?.assigned_to ?? admission?.assigned_to ?? null,
    createdAt: lead?.created_at ?? admission!.created_at,
    note: admission?.note ?? lead?.note ?? null,
    refusalReasonId:
      admission?.refusal_reason_id !== undefined
        ? admission.refusal_reason_id
        : (lead?.refusal_reason_id ?? null),
    refusalReason:
      admission?.refusal_reason !== undefined
        ? admission.refusal_reason
        : (lead?.refusal_reason ?? null),
  };
}

export function buildCandidates(leads: Lead[], admissions: Admission[]): Candidate[] {
  const byId = new Map(admissions.map((item) => [item.id, item]));
  const byLead = new Map(admissions.map((item) => [item.lead_id, item]));
  const matched = new Set<string>();
  const result = leads.map((lead) => {
    const admission = byId.get(lead.admission_id ?? '') ?? byLead.get(lead.id) ?? null;
    if (admission) matched.add(admission.id);
    return candidate(lead, admission);
  });
  for (const admission of admissions) {
    if (!matched.has(admission.id)) result.push(candidate(null, admission));
  }
  return result.sort((a, b) => b.createdAt.localeCompare(a.createdAt) || a.id.localeCompare(b.id));
}

export function filterCandidates(candidates: Candidate[], filters: CandidateFilters): Candidate[] {
  const query = filters.query?.trim().toLocaleLowerCase();
  return candidates.filter(
    (item) =>
      (!query ||
        `${item.fullName} ${item.contact} ${item.lead?.course ?? ''}`
          .toLocaleLowerCase()
          .includes(query)) &&
      (!filters.stage || item.stage === filters.stage) &&
      (!filters.subject || item.subjectId === filters.subject) &&
      (!filters.source || item.source?.id === filters.source) &&
      (!filters.assignee || item.assignee?.id === filters.assignee) &&
      (!filters.refusalReason ||
        (item.stage === 'refused' &&
          (filters.refusalReason === '__none__'
            ? !item.refusalReasonId
            : item.refusalReasonId === filters.refusalReason))),
  );
}

export function parseRecruitmentSearch(search: Record<string, unknown>): {
  view?: RecruitmentView;
} {
  return search.view === 'board' || search.view === 'list' || search.view === 'analytics'
    ? { view: search.view }
    : {};
}
